import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { API } from "@/api";
import Header from "@/components/Header";

const SettingsScreen = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);

  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const [showEditName, setShowEditName] = useState(false);
  const [editName, setEditName] = useState(user?.name || '');
  const [nameLoading, setNameLoading] = useState(false);

  const currentLanguage = i18n.language?.startsWith('it') ? 'it' : 'en';

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('forevergym-language', lang);
  };

  useEffect(() => {
    if (user?.id) {
      setProfilePicture(`${API}/auth/profile-picture/${user.id}?t=${Date.now()}`);
    }
  }, [user]);

  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Formato non supportato. Usa JPG, PNG o WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('L\'immagine non può superare i 5MB');
      return;
    }

    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/auth/profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfilePicture(`${API}/auth/profile-picture/${user.id}?t=${Date.now()}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmNewPassword) {
      setPasswordError('Le password non coincidono');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La nuova password deve avere almeno 6 caratteri');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSuccess('Password aggiornata con successo!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Errore nel cambio password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!editName.trim() || editName.trim().length < 2) return;
    setNameLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, { name: editName.trim() });
      setShowEditName(false);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setNameLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm(currentLanguage === 'it' ? 'Vuoi uscire dal tuo account?' : 'Do you want to log out?')) {
      logout();
      navigate('/login');
    }
  };

  const themeOptions = [
    { value: 'light', label: t('settings.light'), icon: '☀️' },
    { value: 'dark', label: t('settings.dark'), icon: '🌙' },
    { value: 'system', label: t('settings.system'), icon: '📱' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20 md:pt-16">
      <Header title={t('settings.title')} showBack />

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Profile Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">{t('settings.profile')}</h3>
          
          <div className="flex items-center gap-4 mb-4">
            {/* Profile Picture */}
            <div 
              className="relative cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-16 h-16 rounded-full overflow-hidden bg-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                {profilePicture ? (
                  <img 
                    src={profilePicture} 
                    alt="Profilo" 
                    className="w-full h-full object-cover"
                    onError={() => setProfilePicture(null)}
                  />
                ) : (
                  <span>{user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}</span>
                )}
              </div>
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              {uploadingPicture && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePictureUpload}
                className="hidden"
                data-testid="profile-picture-input"
              />
            </div>

            <div className="flex-1">
              {showEditName ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 p-2 border border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white"
                    data-testid="edit-name-input"
                  />
                  <button
                    onClick={handleUpdateName}
                    disabled={nameLoading}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium"
                    data-testid="save-name-btn"
                  >
                    {nameLoading ? '...' : t('common.save')}
                  </button>
                  <button
                    onClick={() => { setShowEditName(false); setEditName(user?.name || ''); }}
                    className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{user?.name}</h2>
                  <button
                    onClick={() => setShowEditName(true)}
                    className="text-indigo-600 text-sm hover:underline"
                    data-testid="edit-name-trigger"
                  >
                    {t('common.edit')}
                  </button>
                </div>
              )}
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                {t('settings.changePhoto')}
              </p>
            </div>
          </div>
        </div>

        {/* Password Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">{t('settings.changePassword')}</h3>
          
          {showChangePassword ? (
            <form onSubmit={handleChangePassword} className="space-y-3">
              {passwordError && (
                <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 p-3 rounded-lg text-sm">
                  {passwordSuccess}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.currentPassword')}</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 dark:text-white"
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 dark:text-white"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('settings.confirmNewPassword')}</label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 dark:text-white"
                  required
                  minLength={6}
                  data-testid="confirm-new-password-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmNewPassword('');
                  }}
                  className="flex-1 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 py-3 rounded-xl bg-indigo-600 text-white font-semibold disabled:bg-gray-400"
                  data-testid="submit-change-password"
                >
                  {passwordLoading ? t('common.loading') : t('settings.changePassword')}
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              data-testid="change-password-btn"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">🔒</span>
                <span className="font-medium text-gray-900 dark:text-white">{t('settings.changePassword')}</span>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {/* Theme Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">{t('settings.appearance')}</h3>
          
          <div className="grid grid-cols-3 gap-3">
            {themeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setTheme(opt.value)}
                className={`p-4 rounded-xl text-center transition-all ${
                  theme === opt.value
                    ? 'bg-indigo-600 text-white shadow-lg scale-105'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                data-testid={`theme-${opt.value}`}
              >
                <div className="text-2xl mb-1">{opt.icon}</div>
                <div className="text-sm font-medium">{opt.label}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 text-center">
            {theme === 'system' ? t('settings.theme') + ': ' + t('settings.system') : 
             theme === 'dark' ? t('settings.theme') + ': ' + t('settings.dark') : t('settings.theme') + ': ' + t('settings.light')}
          </p>
        </div>

        {/* Language Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">{t('settings.language')}</h3>
          
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => changeLanguage('it')}
              className={`p-4 rounded-xl text-center transition-all ${
                currentLanguage === 'it'
                  ? 'bg-indigo-600 text-white shadow-lg scale-105'
                  : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
              data-testid="lang-it"
            >
              <div className="text-2xl mb-1">🇮🇹</div>
              <div className="text-sm font-medium">Italiano</div>
            </button>
            <button
              onClick={() => changeLanguage('en')}
              className={`p-4 rounded-xl text-center transition-all ${
                currentLanguage === 'en'
                  ? 'bg-indigo-600 text-white shadow-lg scale-105'
                  : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
              data-testid="lang-en"
            >
              <div className="text-2xl mb-1">🇬🇧</div>
              <div className="text-sm font-medium">English</div>
            </button>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">{t('settings.subscription')}</h3>
          
          <button
            onClick={() => navigate('/abbonamento')}
            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl hover:from-indigo-100 hover:to-purple-100 dark:hover:from-indigo-900/50 dark:hover:to-purple-900/50 transition-colors"
            data-testid="subscription-btn"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">👑</span>
              <div className="text-left">
                <span className="font-medium text-gray-900 dark:text-white block">
                  {user?.subscription_status === 'pro' ? 'ForeverGym Pro' : t('subscription.free')}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {user?.subscription_status === 'pro' ? t('subscription.unlimitedClients') : `${user?.client_count || 0}/${user?.client_limit || 5} ${t('common.clients')}`}
                </span>
              </div>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* App Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">{t('settings.appInfo')}</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('settings.version')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">1.0.3</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-sm text-gray-600 dark:text-gray-400">App</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">ForeverGym</span>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 py-4 rounded-xl font-semibold hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
          data-testid="logout-btn"
        >
          {t('settings.logoutAccount')}
        </button>
      </div>
    </div>
  );
};

export default SettingsScreen;
